import React, { useEffect, useState, useCallback } from 'react';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Wrench, Plus, X, Gauge, CheckCircle2, AlertTriangle, Calendar, DollarSign, Trash2, ChevronRight } from 'lucide-react';
import { toast } from 'sonner';
import { SwissCard, SwissTable, SwissTH, SwissTD, KpiCard, StatusPill, EmptyState } from '../layout/SwissUI';

const API = process.env.REACT_APP_BACKEND_URL;
const f$ = (n) => `$${Number(n || 0).toLocaleString(undefined, { maximumFractionDigits: 0 })}`;
const fMi = (n) => n != null ? `${Number(n).toLocaleString()} mi` : '—';
const fmtDate = (iso) => iso ? new Date(iso).toLocaleDateString() : '—';

const STATUS_STYLE = {
  overdue: 'bg-red-100 text-red-800 border-red-200',
  due: 'bg-amber-100 text-amber-800 border-amber-200',
  in_progress: 'bg-blue-100 text-blue-800 border-blue-200',
  ok: 'bg-green-100 text-green-800 border-green-200',
};

const MaintenanceTab = () => {
  const [dashboard, setDashboard] = useState({ vehicles: [], totals: {} });
  const [vehicleDetail, setVehicleDetail] = useState(null);  // open drawer
  const [defaults, setDefaults] = useState({});
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    fetch(`${API}/api/maintenance/dashboard`).then(r => r.json()).then(setDashboard);
    fetch(`${API}/api/maintenance/intervals/defaults`).then(r => r.json()).then(d => setDefaults(d.defaults || {}));
  }, []);

  useEffect(load, [load]);

  return (
    <SwissCard className="p-4 sm:p-6" testid="maintenance-tab">
      <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
        <div className="flex items-center gap-2">
          <Wrench className="h-5 w-5 text-[#E52B20]" />
          <div>
            <p className="text-[10px] uppercase tracking-[0.2em] text-[#666] font-bold">Fleet · Service</p>
            <h2 className="text-xl font-black tracking-tight">Vehicle Maintenance</h2>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-5 gap-3 mb-5">
        <KpiCard label="Vehicles" value={dashboard.totals.vehicles || 0} testid="maint-kpi-vehicles" />
        <KpiCard label="Overdue" value={dashboard.totals.overdue_vehicles || 0} tone="danger" testid="maint-kpi-overdue" />
        <KpiCard label="Due Soon" value={dashboard.totals.due_vehicles || 0} tone="warn" testid="maint-kpi-due" />
        <KpiCard label="In Shop" value={dashboard.totals.in_progress_vehicles || 0} testid="maint-kpi-in-shop" />
        <KpiCard label="Fleet YTD" value={f$(dashboard.totals.fleet_ytd_spend)} tone="success" testid="maint-kpi-ytd" />
      </div>

      <SwissTable className="" data-testid="maintenance-fleet-table">
        <thead>
          <tr>
            <SwissTH>Vehicle</SwissTH>
            <SwissTH>Category</SwissTH>
            <SwissTH className="text-right">Mileage</SwissTH>
            <SwissTH className="text-center">Schedules</SwissTH>
            <SwissTH className="text-center">Status</SwissTH>
            <SwissTH className="text-right">YTD $</SwissTH>
            <SwissTH></SwissTH>
          </tr>
        </thead>
        <tbody>
          {dashboard.vehicles.map(v => {
            const statusVariant = v.status === 'overdue' ? 'alert' : v.status === 'due' ? 'warn' : v.status === 'in_progress' ? 'active' : 'success';
            const statusLabel = v.status === 'overdue' ? `${v.overdue_count} Overdue` : v.status === 'due' ? `${v.due_count} Due` : v.status === 'in_progress' ? 'In Shop' : 'OK';
            return (
              <tr key={v.vehicle_id} className="hover:bg-[#F9F9FA] cursor-pointer" onClick={() => setVehicleDetail(v)} data-testid={`fleet-row-${v.vehicle_id}`}>
                <SwissTD>
                  <div className="font-bold">{v.vehicle_name || v.vehicle_id}</div>
                  <div className="text-[10px] text-[#666] font-mono">{v.vehicle_id}</div>
                </SwissTD>
                <SwissTD className="text-xs capitalize text-[#666]">{v.category}</SwissTD>
                <SwissTD className="text-right tabular-nums font-mono">{fMi(v.current_mileage)}</SwissTD>
                <SwissTD className="text-center text-xs font-mono">{v.schedule_count}</SwissTD>
                <SwissTD className="text-center">
                  <StatusPill variant={statusVariant}>{statusLabel}</StatusPill>
                </SwissTD>
                <SwissTD className="text-right tabular-nums font-mono">{f$(v.ytd_spend)}</SwissTD>
                <SwissTD className="text-right"><ChevronRight className="h-4 w-4 text-[#666] inline" /></SwissTD>
              </tr>
            );
          })}
          {dashboard.vehicles.length === 0 && (
            <tr><SwissTD colSpan="7"><div className="text-center text-[#666] py-8 text-sm">No vehicles in fleet yet.</div></SwissTD></tr>
          )}
        </tbody>
      </SwissTable>

      {vehicleDetail && (
        <VehicleMaintenanceDrawer
          vehicle={vehicleDetail}
          defaults={defaults}
          onClose={() => setVehicleDetail(null)}
          onSaved={() => { load(); }}
        />
      )}
    </SwissCard>
  );
};

// ─── Drawer (slide from right) ────────────────────────────────────────────
const VehicleMaintenanceDrawer = ({ vehicle, defaults, onClose, onSaved }) => {
  const [detail, setDetail] = useState(null);
  const [activeView, setActiveView] = useState('schedules'); // schedules | logs | mileage
  const [showAdd, setShowAdd] = useState(null); // 'mileage' | 'schedule' | 'log' | { type:'complete', sched }
  const [busy, setBusy] = useState(false);

  const reload = useCallback(() => {
    fetch(`${API}/api/maintenance/vehicle/${vehicle.vehicle_id}`).then(r => r.json()).then(setDetail);
  }, [vehicle.vehicle_id]);

  useEffect(reload, [reload]);

  const closeAll = () => { setShowAdd(null); reload(); onSaved(); };

  if (!detail) return null;

  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex justify-end" onClick={onClose} data-testid="maintenance-drawer">
      <div className="bg-white w-full max-w-2xl h-full overflow-y-auto" onClick={e => e.stopPropagation()}>
        <div className="bg-black text-white px-5 py-4 sticky top-0 z-10 flex items-center justify-between">
          <div>
            <p className="text-[10px] uppercase tracking-widest text-red-500 font-bold">{detail.vehicle.category}</p>
            <h2 className="text-xl font-black tracking-tight">{detail.vehicle.vehicle_name}</h2>
            <p className="text-xs text-gray-400 font-mono">{detail.vehicle.vehicle_id}</p>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-white" data-testid="drawer-close"><X className="h-5 w-5" /></button>
        </div>

        <div className="px-5 py-4 grid grid-cols-3 gap-3 border-b">
          <Tile label="Mileage" value={fMi(detail.current_mileage)} icon={Gauge} />
          <Tile label="Schedules" value={detail.schedules.length} />
          <Tile label="Total Spend" value={f$(detail.total_spend)} accent="green" />
        </div>

        <div className="px-5 py-3 flex flex-wrap gap-2 border-b">
          <Button size="sm" onClick={() => setShowAdd('mileage')} className="bg-black hover:bg-gray-800 text-white" data-testid="add-mileage-btn"><Gauge className="h-3.5 w-3.5 mr-1" /> Log Mileage</Button>
          <Button size="sm" onClick={() => setShowAdd('schedule')} variant="outline" data-testid="add-schedule-btn"><Calendar className="h-3.5 w-3.5 mr-1" /> Add Service</Button>
          <Button size="sm" onClick={() => setShowAdd('log')} variant="outline" data-testid="add-log-btn"><Wrench className="h-3.5 w-3.5 mr-1" /> Log Repair</Button>
        </div>

        <div className="px-5 py-3 flex gap-2 border-b">
          {['schedules', 'logs', 'mileage'].map(v => (
            <button
              key={v}
              onClick={() => setActiveView(v)}
              className={`px-3 py-1 text-xs font-semibold rounded-full ${activeView === v ? 'bg-red-600 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}
              data-testid={`view-${v}`}
            >{v === 'schedules' ? 'Service Schedule' : v === 'logs' ? 'Repair Log' : 'Mileage History'}</button>
          ))}
        </div>

        <div className="p-5">
          {activeView === 'schedules' && (
            <SchedulesList schedules={detail.schedules} onComplete={(sched) => setShowAdd({ type: 'complete', sched })} onDelete={async (id) => {
              if (!window.confirm('Delete this schedule?')) return;
              await fetch(`${API}/api/maintenance/schedule/${id}`, { method: 'DELETE' });
              toast.success('Removed'); reload(); onSaved();
            }} />
          )}
          {activeView === 'logs' && (
            <LogsList logs={detail.logs} onDelete={async (id) => {
              if (!window.confirm('Delete this log entry?')) return;
              await fetch(`${API}/api/maintenance/logs/${id}`, { method: 'DELETE' });
              toast.success('Removed'); reload(); onSaved();
            }} />
          )}
          {activeView === 'mileage' && (
            <MileageList entries={detail.mileage_history} onDelete={async (id) => {
              await fetch(`${API}/api/maintenance/mileage/${id}`, { method: 'DELETE' });
              toast.success('Removed'); reload(); onSaved();
            }} />
          )}
        </div>

        {showAdd === 'mileage' && (
          <AddMileageModal vehicle={vehicle} onClose={() => setShowAdd(null)} onSaved={closeAll} />
        )}
        {showAdd === 'schedule' && (
          <AddScheduleModal vehicle={vehicle} defaults={defaults} currentMileage={detail.current_mileage} onClose={() => setShowAdd(null)} onSaved={closeAll} />
        )}
        {showAdd === 'log' && (
          <AddLogModal vehicle={vehicle} currentMileage={detail.current_mileage} onClose={() => setShowAdd(null)} onSaved={closeAll} />
        )}
        {showAdd && showAdd.type === 'complete' && (
          <CompleteServiceModal sched={showAdd.sched} currentMileage={detail.current_mileage} onClose={() => setShowAdd(null)} onSaved={closeAll} />
        )}
      </div>
    </div>
  );
};

const SchedulesList = ({ schedules, onComplete, onDelete }) => {
  if (schedules.length === 0) return <Empty label="No service schedules yet. Click 'Add Service' to set one up." />;
  return (
    <div className="space-y-2" data-testid="schedules-list">
      {schedules.map(s => (
        <div key={s.schedule_id} className="border rounded-lg p-3 hover:border-gray-300">
          <div className="flex items-start justify-between gap-3">
            <div className="flex-1">
              <div className="flex items-center gap-2">
                <p className="font-bold">{s.service_type}</p>
                <span className={`text-[10px] px-2 py-0.5 rounded-full font-bold border ${STATUS_STYLE[s.status]}`}>{s.status.toUpperCase()}</span>
              </div>
              <p className="text-xs text-gray-500 mt-1">
                Every {s.interval_miles ? `${s.interval_miles.toLocaleString()} mi` : ''}{s.interval_miles && s.interval_months ? ' / ' : ''}{s.interval_months ? `${s.interval_months} mo` : ''}
              </p>
              <p className="text-xs text-gray-400 mt-1">
                Last done: {s.last_done_at ? fmtDate(s.last_done_at) : '—'}{s.last_done_mileage ? ` @ ${fMi(s.last_done_mileage)}` : ''}
                {' · '}
                Next: {s.next_due_mileage ? `${fMi(s.next_due_mileage)}` : ''}{s.next_due_mileage && s.next_due_date ? ' or ' : ''}{s.next_due_date ? fmtDate(s.next_due_date) : ''}
                {s.miles_remaining != null && <span className={`ml-2 font-bold ${s.miles_remaining < 0 ? 'text-red-600' : s.miles_remaining < 500 ? 'text-amber-600' : 'text-gray-700'}`}>({Math.abs(s.miles_remaining).toLocaleString()}mi {s.miles_remaining < 0 ? 'over' : 'left'})</span>}
              </p>
            </div>
            <div className="flex flex-col gap-1 flex-shrink-0">
              <Button size="sm" onClick={() => onComplete(s)} className="bg-green-600 hover:bg-green-700 h-7 text-[11px]" data-testid={`complete-${s.schedule_id}`}>
                <CheckCircle2 className="h-3 w-3 mr-1" /> Done
              </Button>
              <button onClick={() => onDelete(s.schedule_id)} className="text-[10px] text-red-600 hover:underline">Delete</button>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
};

const LogsList = ({ logs, onDelete }) => {
  if (logs.length === 0) return <Empty label="No repair history yet." />;
  return (
    <div className="space-y-2" data-testid="logs-list">
      {logs.map(l => (
        <div key={l.log_id} className="border rounded-lg p-3">
          <div className="flex items-start justify-between gap-3">
            <div className="flex-1">
              <p className="font-bold">{l.service_type} <span className="ml-2 text-[10px] uppercase tracking-wider text-gray-400">{l.kind}</span></p>
              <p className="text-xs text-gray-500 mt-1">
                {fmtDate(l.performed_at)}{l.mileage ? ` · ${fMi(l.mileage)}` : ''}{l.vendor ? ` · ${l.vendor}` : ''}
              </p>
              {l.notes && <p className="text-xs text-gray-600 mt-1">{l.notes}</p>}
            </div>
            <div className="flex flex-col items-end gap-1">
              <p className="font-bold text-green-700">{f$(l.cost)}</p>
              <button onClick={() => onDelete(l.log_id)} className="text-[10px] text-red-600 hover:underline"><Trash2 className="h-3 w-3 inline" /></button>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
};

const MileageList = ({ entries, onDelete }) => {
  if (entries.length === 0) return <Empty label="No mileage readings yet. Log one to start tracking." />;
  return (
    <div className="space-y-1" data-testid="mileage-list">
      {entries.map(e => (
        <div key={e.log_id} className="border rounded p-2 flex items-center justify-between text-sm">
          <span className="font-mono">{fMi(e.mileage)}</span>
          <span className="text-xs text-gray-500">{fmtDate(e.date)} · {e.source}</span>
          <button onClick={() => onDelete(e.log_id)} className="text-[10px] text-red-600 hover:underline">×</button>
        </div>
      ))}
    </div>
  );
};

// ─── Add modals ───────────────────────────────────────────────────────────
const AddMileageModal = ({ vehicle, onClose, onSaved }) => {
  const [mileage, setMileage] = useState('');
  const [busy, setBusy] = useState(false);

  const save = async () => {
    const n = parseFloat(mileage);
    if (!n || n <= 0) { toast.error('Enter a positive mileage'); return; }
    setBusy(true);
    const r = await fetch(`${API}/api/maintenance/mileage`, {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ vehicle_id: vehicle.vehicle_id, mileage: n }),
    });
    if (r.ok) { toast.success(`Logged ${n.toLocaleString()} mi`); onSaved(); } else { toast.error('Save failed'); }
    setBusy(false);
  };

  return (
    <ModalShell title="Log Mileage" onClose={onClose} testid="mileage-modal">
      <Field label="Current odometer (miles)">
        <Input type="number" value={mileage} onChange={e => setMileage(e.target.value)} placeholder="50000" autoFocus data-testid="mileage-input" />
      </Field>
      <ModalActions onClose={onClose} onSave={save} busy={busy} testid="mileage-save" />
    </ModalShell>
  );
};

const AddScheduleModal = ({ vehicle, defaults, currentMileage, onClose, onSaved }) => {
  const [form, setForm] = useState({
    service_type: 'Oil Change',
    interval_miles: 5000,
    interval_months: 6,
    last_done_at: '',
    last_done_mileage: currentMileage || 0,
  });
  const [busy, setBusy] = useState(false);

  const pickType = (t) => {
    const d = defaults[t] || {};
    setForm({ ...form, service_type: t, interval_miles: d.interval_miles ?? form.interval_miles, interval_months: d.interval_months ?? form.interval_months });
  };

  const save = async () => {
    if (!form.service_type.trim()) { toast.error('Pick a service type'); return; }
    setBusy(true);
    const body = {
      vehicle_id: vehicle.vehicle_id,
      vehicle_name: vehicle.vehicle_name,
      ...form,
      interval_miles: parseFloat(form.interval_miles) || 0,
      interval_months: parseInt(form.interval_months) || 0,
      last_done_mileage: parseFloat(form.last_done_mileage) || 0,
      last_done_at: form.last_done_at ? new Date(form.last_done_at).toISOString() : null,
    };
    const r = await fetch(`${API}/api/maintenance/schedule`, {
      method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body),
    });
    if (r.ok) { toast.success('Schedule added'); onSaved(); } else { toast.error('Save failed'); }
    setBusy(false);
  };

  return (
    <ModalShell title="Add Service Schedule" onClose={onClose} testid="schedule-modal">
      <Field label="Service type">
        <select value={form.service_type} onChange={e => pickType(e.target.value)} className="w-full h-10 border border-gray-300 rounded-md px-3 text-sm" data-testid="schedule-type">
          {Object.keys(defaults).map(t => <option key={t}>{t}</option>)}
          <option value="">— Custom —</option>
        </select>
        {!Object.keys(defaults).includes(form.service_type) && (
          <Input value={form.service_type} onChange={e => setForm({ ...form, service_type: e.target.value })} placeholder="Custom service name" className="mt-2" />
        )}
      </Field>
      <div className="grid grid-cols-2 gap-3">
        <Field label="Every X miles"><Input type="number" value={form.interval_miles} onChange={e => setForm({ ...form, interval_miles: e.target.value })} data-testid="schedule-miles" /></Field>
        <Field label="Every X months"><Input type="number" value={form.interval_months} onChange={e => setForm({ ...form, interval_months: e.target.value })} /></Field>
      </div>
      <div className="grid grid-cols-2 gap-3">
        <Field label="Last done date"><Input type="date" value={form.last_done_at} onChange={e => setForm({ ...form, last_done_at: e.target.value })} /></Field>
        <Field label="Last done mileage"><Input type="number" value={form.last_done_mileage} onChange={e => setForm({ ...form, last_done_mileage: e.target.value })} /></Field>
      </div>
      <ModalActions onClose={onClose} onSave={save} busy={busy} testid="schedule-save" />
    </ModalShell>
  );
};

const AddLogModal = ({ vehicle, currentMileage, onClose, onSaved }) => {
  const [form, setForm] = useState({
    service_type: 'Repair',
    performed_at: new Date().toISOString().slice(0, 10),
    mileage: currentMileage || 0,
    cost: 0,
    vendor: '',
    technician: '',
    notes: '',
    kind: 'repair',
  });
  const [busy, setBusy] = useState(false);

  const save = async () => {
    if (!form.service_type.trim()) { toast.error('Describe the work'); return; }
    setBusy(true);
    const r = await fetch(`${API}/api/maintenance/logs`, {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        vehicle_id: vehicle.vehicle_id,
        vehicle_name: vehicle.vehicle_name,
        ...form,
        performed_at: new Date(form.performed_at).toISOString(),
        mileage: parseFloat(form.mileage) || 0,
        cost: parseFloat(form.cost) || 0,
      }),
    });
    if (r.ok) { toast.success('Logged'); onSaved(); } else { toast.error('Save failed'); }
    setBusy(false);
  };

  return (
    <ModalShell title="Log Repair / Service" onClose={onClose} testid="log-modal">
      <Field label="Work performed *"><Input value={form.service_type} onChange={e => setForm({ ...form, service_type: e.target.value })} placeholder="e.g., New brake pads, Wheel alignment" data-testid="log-type" /></Field>
      <div className="grid grid-cols-2 gap-3">
        <Field label="Date"><Input type="date" value={form.performed_at} onChange={e => setForm({ ...form, performed_at: e.target.value })} /></Field>
        <Field label="Mileage"><Input type="number" value={form.mileage} onChange={e => setForm({ ...form, mileage: e.target.value })} /></Field>
      </div>
      <div className="grid grid-cols-2 gap-3">
        <Field label="Cost ($)"><Input type="number" step="0.01" value={form.cost} onChange={e => setForm({ ...form, cost: e.target.value })} data-testid="log-cost" /></Field>
        <Field label="Kind">
          <Select value={form.kind} onValueChange={v => setForm({ ...form, kind: v })}>
            <SelectTrigger><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="repair">Repair</SelectItem>
              <SelectItem value="scheduled">Scheduled service</SelectItem>
              <SelectItem value="inspection">Inspection</SelectItem>
              <SelectItem value="detail">Detail / Wash</SelectItem>
              <SelectItem value="other">Other</SelectItem>
            </SelectContent>
          </Select>
        </Field>
      </div>
      <Field label="Vendor / Shop"><Input value={form.vendor} onChange={e => setForm({ ...form, vendor: e.target.value })} placeholder="Master Tech ATL" /></Field>
      <Field label="Technician"><Input value={form.technician} onChange={e => setForm({ ...form, technician: e.target.value })} /></Field>
      <Field label="Notes"><Input value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })} /></Field>
      <ModalActions onClose={onClose} onSave={save} busy={busy} testid="log-save" />
    </ModalShell>
  );
};

const CompleteServiceModal = ({ sched, currentMileage, onClose, onSaved }) => {
  const [form, setForm] = useState({ mileage: currentMileage || 0, cost: 0, vendor: '', notes: '' });
  const [busy, setBusy] = useState(false);

  const save = async () => {
    setBusy(true);
    const r = await fetch(`${API}/api/maintenance/schedule/${sched.schedule_id}/complete`, {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        mileage: parseFloat(form.mileage) || 0,
        cost: parseFloat(form.cost) || 0,
        vendor: form.vendor, notes: form.notes,
      }),
    });
    if (r.ok) { toast.success(`${sched.service_type} marked complete`); onSaved(); } else { toast.error('Save failed'); }
    setBusy(false);
  };

  return (
    <ModalShell title={`Complete: ${sched.service_type}`} onClose={onClose} testid="complete-modal">
      <Field label="Mileage at service"><Input type="number" value={form.mileage} onChange={e => setForm({ ...form, mileage: e.target.value })} autoFocus /></Field>
      <Field label="Cost ($)"><Input type="number" step="0.01" value={form.cost} onChange={e => setForm({ ...form, cost: e.target.value })} /></Field>
      <Field label="Vendor"><Input value={form.vendor} onChange={e => setForm({ ...form, vendor: e.target.value })} /></Field>
      <Field label="Notes"><Input value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })} /></Field>
      <ModalActions onClose={onClose} onSave={save} busy={busy} testid="complete-save" saveLabel="Mark Complete" />
    </ModalShell>
  );
};

// ─── Tiny UI helpers ──────────────────────────────────────────────────────
const Field = ({ label, children }) => (
  <div className="mb-3">
    <label className="text-[10px] font-bold uppercase tracking-wider text-gray-500">{label}</label>
    <div className="mt-1">{children}</div>
  </div>
);

const Tile = ({ label, value, accent, icon: Icon }) => {
  const map = { red: 'text-red-700', amber: 'text-amber-800', green: 'text-green-700', blue: 'text-blue-700' };
  return (
    <div className="bg-gray-50 border border-gray-200 rounded-lg px-3 py-2">
      <p className="text-[10px] font-bold uppercase tracking-wider text-gray-500 flex items-center gap-1">{Icon && <Icon className="h-3 w-3" />}{label}</p>
      <p className={`mt-0.5 text-xl font-black ${map[accent] || 'text-gray-900'}`}>{value}</p>
    </div>
  );
};

const ModalShell = ({ title, onClose, children, testid }) => (
  <div className="fixed inset-0 z-[60] bg-black/60 flex items-center justify-center p-4" onClick={onClose}>
    <div className="bg-white rounded-xl w-full max-w-md max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()} data-testid={testid}>
      <div className="px-5 py-3 border-b flex items-center justify-between">
        <h3 className="font-bold">{title}</h3>
        <button onClick={onClose}><X className="h-5 w-5 text-gray-400" /></button>
      </div>
      <div className="p-5">{children}</div>
    </div>
  </div>
);

const ModalActions = ({ onClose, onSave, busy, testid, saveLabel = 'Save' }) => (
  <div className="flex justify-end gap-2 mt-3">
    <Button variant="outline" onClick={onClose} disabled={busy}>Cancel</Button>
    <Button onClick={onSave} disabled={busy} className="bg-red-600 hover:bg-red-700" data-testid={testid}>{busy ? 'Saving…' : saveLabel}</Button>
  </div>
);

const Empty = ({ label }) => (
  <div className="text-center py-8 text-sm text-gray-400 italic border border-dashed rounded-lg">{label}</div>
);

export default MaintenanceTab;
